// Left menu for Security Operations (/soc). Each entry is a view of data the page already loads;
// the selected view lives in the URL (?view=key), so /soc itself still opens the Overview and every
// view can be linked to, bookmarked and reached with Back.
//
// Grouped as the design's menu (Investigate / Detect / Threat intelligence / Configuration). Entries
// the design shows without any data behind them in Axaris (action center, investigations, threat
// hunting) are left out rather than shipped empty; areas Axaris has that the design lacked (Network,
// SIEM, Data) are added. An area view needs its summary tile: when the
// tile is missing from the summary the entry is hidden.
//
// View fields:
//   tile      - summary tile the view shows (SocTile)
//   metricKeys- show only these metrics of the tile (sub-areas of External threat)
//   link      - "View details" target, overriding the tile config's link
//   queue     - { area } or { source } filter for the incident queue; no queue when absent
//   charts    - trend charts: newFindings / external / controls / network / identity
//   findings  - New findings series fields to chart
//   bandKeys  - External activity bands (by summary metric key) to chart
//   featured  - drawn as a highlighted entry (on a view, or a whole group) - used for Agentic AI
//   note      - why the view has no queue: its figures are checks or counts, not queue items
//   cves      - shows the Open vulnerabilities list (CVEs) under the tile

const CHECKS_NOTE = "These figures are configuration checks from your connected tools, not incidents, so they aren't in the incident queue.";
const ENDPOINT_NOTE = "CVEs are listed under Open vulnerabilities below. They and the endpoint recommendations aren't in the incident queue.";
const VULN_NOTE = "Each CVE in the list below opens to the devices it is on and their risk details. CVEs aren't in the incident queue.";
const COUNTS_NOTE = "These figures are counts read from the connected tool, not items Axaris tracks, so they aren't in the incident queue.";

export const SOC_VIEW_GROUPS = [
  {
    label: "Investigate",
    views: [
      { key: "overview", label: "Overview" },
      {
        key: "incidents", label: "Incidents & alerts", tile: "incidents", queue: { area: "incidents" },
        charts: ["newFindings"], findings: ["incidents", "alerts"],
      },
    ],
  },
  {
    // Its own section right under Investigate, highlighted, so AI agents stand out in the menu.
    label: "AI security",
    featured: true,
    views: [{ key: "agenticAi", label: "Agentic AI", tile: "agenticAi", featured: true, queue: { area: "agenticAi" } }],
  },
  {
    label: "Detect",
    views: [
      { key: "email", label: "Email", tile: "email", note: CHECKS_NOTE },
      { key: "identity", label: "Identities", tile: "identity", charts: ["identity"], note: CHECKS_NOTE },
      {
        key: "endpoint", label: "Endpoints", tile: "endpoint",
        charts: ["newFindings"], findings: ["vulnerabilities", "otherDefender"], note: ENDPOINT_NOTE, cves: true,
      },
      {
        // The CVEs on their own: the Endpoint tile's CVE figures, the vulnerability trend and the list.
        key: "vulnerabilities", label: "Vulnerabilities", tile: "endpoint", title: "Vulnerabilities",
        metricKeys: ["openCves", "criticalCves", "cveInstances", "exposedDevices"],
        charts: ["newFindings"], findings: ["vulnerabilities"], note: VULN_NOTE, cves: true,
      },
      { key: "cloud", label: "Cloud", tile: "cloud", note: CHECKS_NOTE },
      { key: "cloudApps", label: "Cloud apps", tile: "cloudApps", note: COUNTS_NOTE },
      { key: "network", label: "Network", tile: "network", charts: ["network"], note: COUNTS_NOTE },
      { key: "siem", label: "SIEM", tile: "siem", note: COUNTS_NOTE },
      { key: "data", label: "Data", tile: "data", note: COUNTS_NOTE },
      {
        key: "code", label: "Code & DevOps", tile: "external", title: "Code & DevOps",
        metricKeys: ["secretsEvents", "openSecretsRisks"], link: "/DarkWebSecretsScan",
        queue: { source: "Exposed secret" }, charts: ["external"], bandKeys: ["secretsEvents"],
      },
    ],
  },
  {
    label: "Threat intelligence",
    views: [
      {
        key: "external", label: "External threat", tile: "external", queue: { area: "external" },
        charts: ["external", "newFindings"], findings: ["external"],
      },
      {
        key: "darkweb", label: "Dark web", tile: "external", title: "Dark web",
        metricKeys: ["darkWebEvents", "openLeakedCredentials"], link: "/DarkWebMonitoringDashboard",
        queue: { source: "Leaked credential" }, charts: ["external"], bandKeys: ["darkWebEvents"],
      },
    ],
  },
  {
    label: "Configuration",
    views: [
      { key: "connectors", label: "Data connectors" },
      { key: "detectionRules", label: "Detection rules", tile: "detectionRules", note: CHECKS_NOTE },
    ],
  },
];

export const SOC_VIEWS = Object.fromEntries(
  SOC_VIEW_GROUPS.flatMap((g) => g.views).map((v) => [v.key, v])
);

// Unknown or unavailable keys fall back to the Overview, so an old or mistyped link still lands.
export const resolveView = (key, tiles) => {
  const view = SOC_VIEWS[key];
  if (!view) return SOC_VIEWS.overview;
  if (view.tile && !(tiles || []).some((t) => t.key === view.tile)) return SOC_VIEWS.overview;
  return view;
};

// The menu as rendered: groups with their available entries and counts.
//   counts: { incidents, external, connectors, agenticAi } - the menu's badges.
export const buildNav = (tiles, counts = {}) =>
  SOC_VIEW_GROUPS.map((g) => ({
    label: g.label,
    featured: !!g.featured,
    views: g.views
      .filter((v) => !v.tile || (tiles || []).some((t) => t.key === v.tile))
      .map((v) => ({
        key: v.key,
        label: v.label,
        count: counts[v.key] ?? null,
        featured: !!v.featured,
        tone: v.key === "incidents" ? "hot" : v.key === "connectors" ? "warn" : v.featured ? "ai" : "plain",
      })),
  })).filter((g) => g.views.length > 0);
